/**
 * Every page of the app, grouped for the navigation drawer. A page that is opened from another
 * page sits in that page's children.
 *
 *   name: the route name
 *   summary: what the page is for, in a few words
 *   dates: the link carries the dates being looked at. 'production' adds ?dates=&production=,
 *     'week' adds ?dates= for the start of the week, 'meals' puts the date in the path (Meals)
 *   needs: the page takes an id in its path; names the page it is opened from. The menu leaves
 *     it out (it can't link to it), lifting up the pages under it; a starred or recent visit to
 *     one keeps its id and is listed
 *
 * A new route goes in here too (tests/unit/nav.spec.js checks that every route is listed).
 */
export const navSections = [
  {
    title: 'Production',
    icon: 'mdi-calendar-check',
    summary: 'What to make for the production days',
    items: [
      {
        name: 'Meals', dates: 'meals', summary: 'Meals in production for a date, with images',
        children: [
          {name: 'MealDetail', needs: 'Meals', summary: 'Full recipe, ingredients and nutrients of one meal'},
          {name: 'MealDetail2', needs: 'a meal link', summary: 'Alternate meal detail on the newer components; nothing links here'},
          {name: 'MealDetailMulti', needs: 'Meals', summary: 'Several meals side by side, per diet and time of day'},
          {name: 'MealNutrientsMulti', needs: 'Meals or Meal Design', summary: 'Nutrients of selected meals against the diet targets'},
          {name: 'MealPrepWork', summary: 'A meal\'s prep instructions, without nutrients or cost'},
        ],
      },
      {name: 'Orders', dates: 'production', summary: 'Orders for the days, filtered by stream or time of day; csv'},
      {name: 'OrderEdit', dates: 'production', summary: 'Cancel, add or replace meals on a customer\'s order, even past the deadline'},
      {name: 'Shopping', summary: 'Ingredients to buy for a date range'},
      {name: 'Chopping', summary: 'Ingredients to chop for the days, allergy meals excluded'},
      {name: 'Precut', dates: 'production', summary: 'Precut ingredient prep list'},
      {name: 'Component Demand', dates: 'production', summary: 'Cooked amount per component and each meal\'s share'},
      {name: 'ComponentPrep2', dates: 'production', summary: 'Prep sheet by team and diet, with filing breakdown'},
      {name: 'ComponentPrep', dates: 'production', summary: 'Older component prep sheet: steps, teams, priorities'},
      {name: 'ComponentUsage', dates: 'production', summary: 'Components each production\'s meals use; replace them'},
      {name: 'Platings', dates: 'production', summary: 'Printable plating sheets per meal'},
      {name: 'PlatingTV', dates: 'production', summary: 'TV display for the plating line'},
      {name: 'PlayMacros', summary: 'Print the starred print macros for the production days'},
      {
        name: 'Reports', dates: 'production', summary: 'Index of printable kitchen reports',
        children: [
          {name: 'ChefsChoiceDemand', summary: 'Chef\'s choice meal counts, local vs shipping'},
        ],
      },
    ],
  },
  {
    title: 'Team tasks',
    icon: 'mdi-clipboard-check-outline',
    summary: 'Task screens the kitchen teams work through',
    items: [
      {name: 'ChoppingTasks', summary: 'Chopping team\'s prep tasks, with late warnings'},
      {name: 'ComponentTasks', dates: 'production', summary: 'Component cooking tasks by priority'},
      {name: 'ComponentShortTasks', dates: 'production', summary: 'Component tasks for shorts only'},
      {name: 'PlatingTasks2', dates: 'production', summary: 'Plating tasks with allergy combinations and shorts'},
      {name: 'PlatingTasks', dates: 'production', summary: 'Older plating tasks by rack or meal'},
      {name: 'AllergyPlatingTasks', dates: 'production', summary: 'Plating tasks for allergy meals'},
      {name: 'SealingTasks', dates: 'production', summary: 'Sealing team tasks by rack'},
      {
        name: 'PackingTasks', dates: 'production', summary: 'Packing team tasks',
        children: [
          {name: 'PackingTasksAdmin', summary: 'Packing tasks with a button to clear them; not for normal use'},
        ],
      },
      {name: 'MealScan', summary: 'Tablet page for scanning meals with the QR scanner'},
      {name: 'TaskReport2', dates: 'production', summary: 'Completed tasks with durations, linking to analysis'},
      {name: 'TaskReport', summary: 'Older, simple completed task report'},
      {
        name: 'TaskList', summary: 'Chopping tasks with their actual kg/hr rates',
        children: [
          {name: 'TaskAnalysis', needs: 'Task List or Task Report 2', summary: 'Chopping durations and rates for one ingredient'},
        ],
      },
      {name: 'TaskSettings', summary: 'Deadline times for late task warnings'},
      {name: 'FoodPrepAssignment', summary: 'Set the prep team for an ingredient across meals'},
    ],
  },
  {
    title: 'Weighing & audits',
    icon: 'mdi-scale',
    summary: 'Weigh components in, log waste and shorts',
    items: [
      {
        name: 'ComponentAudit', dates: 'production', summary: 'Weigh and log filed component amounts',
        children: [
          {name: 'Container', summary: 'Containers and their tare weights'},
        ],
      },
      {name: 'ComponentWaste', dates: 'production', summary: 'Weigh and log leftover component waste'},
      {name: 'AuditedShorts', dates: 'production', summary: 'Component shorts found while filing'},
      {name: 'ComponentShort', summary: 'Duplicate of Audited Shorts, due to be removed'},
    ],
  },
  {
    title: 'Packing & delivery',
    icon: 'mdi-truck-delivery-outline',
    summary: 'Labels, packing, racks and drivers',
    items: [
      {name: 'Labels', dates: 'production', summary: 'Print meal labels, per size or restriction'},
      {name: 'LabelsBasic', summary: 'Simple label printing for one date'},
      {name: 'LabelSearch', dates: 'production', summary: 'Find orders by meal, customer or bin and reprint labels'},
      {
        name: 'Packing', dates: 'production', summary: 'Packing lists by customer, meal or rack; box stickers',
        children: [
          {name: 'RackAssignment', summary: 'Delivery racks and the rules for assigning boxes'},
          {name: 'BinDistribution', dates: 'production', summary: 'How many boxes and how much rack space orders need'},
          {name: 'PackingAdmin', summary: 'Packing in admin mode: date range, Onfleet export'},
        ],
      },
      {name: 'Delivery2', dates: 'production', summary: 'Deliveries for the days, Onfleet export and pdf'},
      {name: 'DeliveryAdmin2', summary: 'Deliveries in admin mode, with destructive Onfleet actions'},
      {name: 'DeliveryAdmin', summary: 'Old delivery screen in admin mode'},
      {name: 'Drivers', summary: 'Drivers\' replies to the 7am text, with ETAs'},
      {name: 'DriverMileage', summary: 'Driver mileage from Onfleet for a date range'},
    ],
  },
  {
    title: 'Meals & recipes',
    icon: 'mdi-silverware-fork-knife',
    summary: 'Design meals, their versions, images and checks',
    items: [
      {
        name: 'MealDesign', summary: 'Create, copy and edit meals',
        children: [
          {name: 'MealDesignAdmin', summary: 'Edit meals that are dated or past the cutoff'},
        ],
      },
      {name: 'MealDesigner', summary: 'Build a meal from existing or new components'},
      {name: 'MealAudit', dates: 'production', summary: 'Find and fix problems in upcoming meals'},
      {
        name: 'MealVersions', summary: 'Meal SKUs and versions; find unused SKUs',
        children: [
          {name: 'MealSku', needs: 'Meal Versions', summary: 'Every version of one SKU against the reference meal'},
        ],
      },
      {name: 'MealImageList', summary: 'Meals by diet with their images'},
      {name: 'MealImages', summary: 'Upload and remove a meal\'s photos'},
      {name: 'CustomerMealInstructions', summary: 'Customer instructions for meals; find meals missing them'},
      {name: 'MealFilterList', summary: 'Filter meals by diet or nutrients; saved filters'},
      {name: 'MealIngredientModifier', summary: 'Replace or add an ingredient across many meals'},
      {name: 'UncookedIngredients', summary: 'Find raw ingredients in meals and replace them'},
    ],
  },
  {
    title: 'Components & ingredients',
    icon: 'mdi-food-apple-outline',
    summary: 'Recipes, ingredients, nutrients and diets',
    items: [
      {
        name: 'Components', summary: 'Search and browse components',
        children: [
          {
            name: 'ComponentDetail', needs: 'Components', summary: 'A component\'s recipe, nutrients and meals',
            children: [
              {name: 'ComponentDetailAdmin', summary: 'Edit a component\'s recipe and details'},
            ],
          },
        ],
      },
      {name: 'ComponentComparison', summary: 'Two components side by side, per 100g'},
      {name: 'Ingredients', summary: 'Search ingredients with nutrient columns; bulk tag'},
      {name: 'IngredientNutrients', summary: 'Edit an ingredient\'s nutrients; clone ingredients'},
      {name: 'NutrientTargets', summary: 'Diets and their nutrient targets and limits'},
      {name: 'DayNutrition', dates: 'week', summary: 'How each day\'s meals meet the RDAs and diet targets; swaps between diets'},
      {name: 'Restrictions', summary: 'Allergies and restrictions offered at signup'},
    ],
  },
  {
    title: 'Menus & schedule',
    icon: 'mdi-calendar-month-outline',
    summary: 'Which meals run when, and the customer menu',
    items: [
      {name: 'MealSchedule', summary: 'Assign meals to dates, times of day and priority'},
      {name: 'MenuScheduler', summary: 'Schedule SKUs onto menu days'},
      {
        name: 'MealScheduleTemplate', summary: 'Schedule templates; replaced by an automated job',
        children: [
          {name: 'MealTemplate', needs: 'Meal Schedule Template', summary: 'A meal\'s components; create missing ones'},
        ],
      },
      {
        name: 'Menu', summary: 'Generate and preview the customer menu',
        children: [
          {name: 'MealPhotos', summary: 'Browse the published menus\' meal photos; copy links at any size'},
          {name: 'MenuEdit', summary: 'Static menu items and categories (chef\'s choice menu)'},
          {name: 'IngredientStopWords', summary: 'Words hidden from customer ingredient lists'},
          {name: 'SignupOptions', summary: 'Signup diet / allergy options and the allergy settings each applies'},
        ],
      },
      {name: 'MenuBuilder', summary: 'Build a new diet\'s menu from meals in production'},
      {name: 'MenuCmsExport', summary: 'A week\'s menu per stream as csv for the website'},
      {name: 'Streams', summary: 'Diet streams and whether they are shown'},
    ],
  },
  {
    title: 'Customers',
    icon: 'mdi-account-group-outline',
    summary: 'Customer records, feedback and emails',
    items: [
      {
        name: 'Customers', summary: 'Look up a customer',
        children: [
          {
            name: 'Customer', needs: 'Customers', summary: 'Edit a customer\'s profile, addresses and notes',
            children: [
              {name: 'StripeCustomer', needs: 'Customer', summary: 'A customer\'s Stripe payments and receipts'},
              {name: 'CustomerTracking', needs: 'Customer', summary: 'Timeline of a customer\'s app and web events'},
            ],
          },
        ],
      },
      {
        name: 'CustomersFinance', summary: 'All customers with finance columns; bulk edit',
        children: [
          {name: 'CustomerFinance', needs: 'Customers Finance', summary: 'Customer page with plan, delete and commission'},
        ],
      },
      {name: 'CustomerAnalytics', summary: 'Customer charts and stats'},
      {name: 'CustomerAnalyticsFinance', summary: 'Customer analytics with sales'},
      {name: 'CustomerTrackingSummary', summary: 'Tracked events by category'},
      {name: 'CustomerCancelReasons', summary: 'Why customers cancelled, by date range'},
      {name: 'Feedback', dates: 'week', summary: 'Customers\' meal feedback for the week'},
      {name: 'WebAppFeedback', summary: 'Web app feedback by date range'},
      {
        name: 'CustomerEmailLog', summary: 'Emails sent to customers',
        children: [
          {name: 'CustomerEmailDetail', needs: 'Customer Email Log', summary: 'Preview one sent email'},
        ],
      },
      {name: 'EmailTemplateEditor', summary: 'Edit, version and test customer email templates'},
      {name: 'EmailBroadcast', summary: 'Write a one-off email and send it to a group of customers'},
    ],
  },
  {
    title: 'Sales & billing',
    icon: 'mdi-cash-register',
    summary: 'Plans, prices, promotions and invoices',
    items: [
      {
        name: 'Plans', summary: 'Subscription plans',
        children: [
          {name: 'PlanEdit', needs: 'Plans', summary: 'A plan\'s prices and ranges'},
        ],
      },
      {name: 'Promotions', summary: 'Promotions and referral codes'},
      {name: 'CorporateCodes', summary: 'Corporate companies, codes and addresses'},
      {name: 'ShippingRules', summary: 'Pickup addresses and shipping rates per city'},
      {name: 'Postcodes', summary: 'Postcode regions and excluded postcodes'},
      {name: 'Invoices', summary: 'Invoices by week or month, year on year'},
      {name: 'CustomerInvoiceComparison', summary: 'One customer\'s invoice week against Stripe'},
      {name: 'Coupons', summary: 'Coupons and gift cards: search, redemptions, both Stripe accounts'},
      {name: 'Metrics', summary: 'Orders and churn by plan or stream over weeks'},
    ],
  },
  {
    title: 'Admin',
    icon: 'mdi-cog-outline',
    summary: 'Users, roles and maintenance',
    items: [
      {name: 'Home', summary: 'Welcome page'},
      {name: 'Users', summary: 'Add users, set roles and passwords'},
      {name: 'Roles', summary: 'Roles and the pages each can read or write'},
      {name: 'ActiveUsers', summary: 'Who is using the kitchen: machine, email, role, IP'},
      {name: 'PrintMacros', summary: 'Record and play print macros'},
      {name: 'CheckConsistency', summary: 'Compare a week\'s orders across systems and fix them'},
      {name: 'CacheClear', summary: 'Reload the order cache from Firebase'},
      {name: 'Reset', summary: 'Emergency api server reset'},
    ],
  },
];
